// src/components/common/ExportModal.tsx
// Modal reutilizable para exportar Excel con selección de campos
// Usado en: ClientsListPage, BestClientsPage, InactiveClientsPage

import React, { useState, useCallback } from 'react';
import { X, FileDown, CheckSquare, Square, Info } from 'lucide-react';

// ========================================================================
// TIPOS
// ========================================================================

export interface ExportField {
  /** Identificador único del campo (e.g. 'email', 'gender') */
  key: string;
  /** Texto que aparece como header en el Excel */
  header: string;
  /** Etiqueta visible en el checkbox del modal */
  label: string;
  /** Si true, siempre se incluye y el checkbox aparece deshabilitado */
  alwaysIncluded?: boolean;
  /** Si true, el checkbox inicia marcado (default: true para opcionales) */
  defaultSelected?: boolean;
  /** Ancho sugerido para la columna en Excel (wch) */
  columnWidth?: number;
}

export interface ExportModalProps {
  /** Controla visibilidad del modal */
  isOpen: boolean;
  /** Callback para cerrar el modal */
  onClose: () => void;
  /** Callback que recibe los keys seleccionados al confirmar */
  onExport: (selectedKeys: string[]) => void;
  /** Lista de campos disponibles para exportar */
  fields: ExportField[];
  /** Título del modal (default: 'Exportar a Excel') */
  title?: string;
  /** Texto descriptivo debajo del título */
  description?: string;
  /** Texto del botón de exportar (default: 'Descargar Excel') */
  exportButtonLabel?: string;
  /** Si true, deshabilita el botón de exportar (e.g. mientras se genera) */
  isExporting?: boolean;
  /** Total de registros que se exportarán (informativo) */
  totalRecords?: number;
}

// ========================================================================
// COMPONENTE
// ========================================================================

const ExportModal: React.FC<ExportModalProps> = ({
  isOpen,
  onClose,
  onExport,
  fields,
  title = 'Exportar a Excel',
  description = 'Selecciona los campos que deseas incluir en el archivo.',
  exportButtonLabel = 'Descargar Excel',
  isExporting = false,
  totalRecords
}) => {
  // Inicializar selección: alwaysIncluded = true, opcionales = defaultSelected ?? true
  const getInitialSelection = useCallback((): Record<string, boolean> => {
    const initial: Record<string, boolean> = {};
    fields.forEach(field => {
      if (field.alwaysIncluded) {
        initial[field.key] = true;
      } else {
        initial[field.key] = field.defaultSelected !== undefined ? field.defaultSelected : true;
      }
    });
    return initial;
  }, [fields]);

  const [selection, setSelection] = useState<Record<string, boolean>>(getInitialSelection);

  // Reset selección cuando se abre el modal
  React.useEffect(() => {
    if (isOpen) {
      setSelection(getInitialSelection());
    }
  }, [isOpen, getInitialSelection]);

  // Toggle individual
  const toggleField = (key: string) => {
    const field = fields.find(f => f.key === key);
    if (field?.alwaysIncluded) return;
    setSelection(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Seleccionar / deseleccionar todos los opcionales
  const toggleAll = (selectAll: boolean) => {
    setSelection(prev => {
      const next = { ...prev };
      fields.forEach(field => {
        if (!field.alwaysIncluded) {
          next[field.key] = selectAll;
        }
      });
      return next;
    });
  };

  // Confirmar exportación
  const handleExport = () => {
    const selectedKeys = fields
      .filter(field => selection[field.key])
      .map(field => field.key);
    onExport(selectedKeys);
  };

  // Contadores
  const optionalFields = fields.filter(f => !f.alwaysIncluded);
  const selectedOptionalCount = optionalFields.filter(f => selection[f.key]).length;
  const allOptionalsSelected = selectedOptionalCount === optionalFields.length;
  const totalSelectedCount = fields.filter(f => selection[f.key]).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div className="modal-overlay fixed inset-0 bg-black bg-opacity-40" onClick={onClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="modal-panel relative bg-surface rounded-xl shadow-xl w-full max-w-md">
          
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-line">
            <div className="flex items-center">
              <FileDown className="h-5 w-5 text-green-600 dark:text-green-400 mr-2.5" />
              <h3 className="text-lg font-semibold text-content">{title}</h3>
            </div>
            <button
              onClick={onClose}
              disabled={isExporting}
              className="text-content-3 hover:text-content-2 transition-colors p-1 rounded-lg hover:bg-canvas"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4">
            <p className="text-sm text-content-2 mb-4">{description}</p>

            {/* Info de registros */}
            {totalRecords !== undefined && totalRecords > 0 && (
              <div className="flex items-center bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 text-xs px-3 py-2 rounded-lg mb-4">
                <Info className="h-3.5 w-3.5 mr-1.5 flex-shrink-0" />
                Se exportarán hasta <strong className="mx-1">{totalRecords.toLocaleString()}</strong> registros.
              </div>
            )}

            {/* Toggle todos */}
            {optionalFields.length > 1 && (
              <div className="flex items-center justify-between mb-3 pb-3 border-b border-line">
                <span className="text-xs font-medium text-content-3 uppercase tracking-wide">Campos</span>
                <button
                  onClick={() => toggleAll(!allOptionalsSelected)}
                  className="text-xs text-accent hover:text-blue-800 dark:text-blue-300 font-medium transition-colors"
                >
                  {allOptionalsSelected ? 'Deseleccionar opcionales' : 'Seleccionar todos'}
                </button>
              </div>
            )}

            {/* Lista de campos */}
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {fields.map(field => {
                const isSelected = selection[field.key];
                const isDisabled = field.alwaysIncluded || isExporting;

                return (
                  <button
                    key={field.key}
                    type="button"
                    onClick={() => toggleField(field.key)}
                    disabled={isDisabled}
                    className={`w-full flex items-center px-3 py-2.5 rounded-lg text-left transition-colors ${
                      isDisabled
                        ? 'cursor-default bg-surface-2'
                        : isSelected
                          ? 'bg-green-50 dark:bg-green-500/10 hover:bg-green-100 dark:hover:bg-green-500/20'
                          : 'hover:bg-surface-2'
                    }`}
                  >
                    {isSelected ? (
                      <CheckSquare className={`h-4 w-4 mr-3 flex-shrink-0 ${
                        field.alwaysIncluded ? 'text-content-3' : 'text-green-600 dark:text-green-400'
                      }`} />
                    ) : (
                      <Square className="h-4 w-4 mr-3 flex-shrink-0 text-content-3" />
                    )}
                    <span className={`text-sm ${
                      isSelected ? 'text-content font-medium' : 'text-content-3'
                    }`}>
                      {field.label}
                    </span>
                    {field.alwaysIncluded && (
                      <span className="ml-auto text-xs text-content-3">siempre</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-line bg-surface-2 rounded-b-xl">
            <span className="text-xs text-content-3">
              {totalSelectedCount} {totalSelectedCount === 1 ? 'columna' : 'columnas'} seleccionadas
            </span>
            <div className="flex gap-2">
              <button
                onClick={onClose}
                disabled={isExporting}
                className="px-4 py-2 text-sm font-medium text-content-2 bg-surface border border-line rounded-lg hover:bg-surface-2 transition-colors disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleExport}
                disabled={isExporting || totalSelectedCount === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExporting ? (
                  <>
                    <svg className="animate-spin -ml-0.5 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Generando...
                  </>
                ) : (
                  <>
                    <FileDown className="h-4 w-4 mr-1.5" />
                    {exportButtonLabel}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;